import React, { useContext, CSSProperties } from "react";
import clsx from "clsx";
import get from "lodash.get";
import { ColDef } from "ag-grid-community";
import { FieldViewData, IFormattedViewContext, FormattedViewContext, CorreColDef, isSystemColExtended } from "./colDef";
import { isEmptyValue, stringify } from "../../utils";

type UIDisplayAdditionalProps = {
	rowClassName?: string;
	singleLineDisplay?: boolean;
	noSingleLineDisplay?: boolean;
	displayClassName?: string;
	displayEmptyMsg?: boolean | React.ReactNode;
	displayDefaultEmptyMsg?: boolean;
	displayContentOnly?: boolean;
	omitFieldIfEmpty?: boolean;
};

export const UIDisplayFieldData: React.FC<
	{
		colId: string;
		displayRaw?: boolean;
		overrideHeader?: string;
		omitLabel?: boolean;
		showLabel?: boolean;
		omitWrapper?: boolean;
	} & UIDisplayAdditionalProps
> = (props) => {
	const context: IFormattedViewContext = useContext(FormattedViewContext);
	if (!context || !context.viewData) return null;
	const fieldViewData: FieldViewData = get(context.viewData, props.colId);
	if (!fieldViewData) {
		console.warn("No view data for colId:", props.colId);
		return null;
	}
	const displayLbl: string = props.overrideHeader || fieldViewData.display;
	const isEmpty: boolean = props.displayRaw ? fieldViewData.isEmpty : isEmptyValue(fieldViewData.formattedValue);
	if (props.omitFieldIfEmpty && isEmpty) return null;
	if ((props.omitLabel || context.omitLabels) && !props.showLabel) {
		if (props.displayEmptyMsg && isEmpty) {
			return (
				<UIEmptyFieldData name={props.displayDefaultEmptyMsg ? undefined : displayLbl}>
					{!props.displayDefaultEmptyMsg && props.displayEmptyMsg !== true && props.displayEmptyMsg}
				</UIEmptyFieldData>
			);
		}
		if (props.omitWrapper) {
			if (props.displayRaw) {
				return (fieldViewData.value || "").toString();
			}
			return (fieldViewData.formattedValue as any) || null;
		}
		return (
			<span onClick={fieldViewData.onClick} className={fieldViewData.className}>
				{props.displayRaw ? (fieldViewData.value || "").toString() : fieldViewData.formattedValue}
			</span>
		);
	}

	return (
		<UIDisplayField
			{...fieldViewData}
			value={props.displayRaw ? fieldViewData.value : fieldViewData.formattedValue}
			display={displayLbl}
			loading={context.loading}
			hideLoadingText={context.hideLoadingText}
			key={props.overrideHeader || fieldViewData.display}
			rowClassName={props.rowClassName}
			headerClassName={fieldViewData.headerClassName}
			singleLineDisplay={props.singleLineDisplay}
			displayClassName={props.displayClassName}
			displayEmptyMsg={props.displayEmptyMsg && isEmpty}
			displayContentOnly={props.displayContentOnly}
			id={props.colId}
		/>
	);
};

export const UIDisplayField: React.FC<
	{
		display?: string | React.ReactNode;
		value: string | React.ReactNode;
		loading?: boolean;
		hideLoadingText?: boolean;
		id: string;
		className?: string;
		headerClassName?: string;
		onClick?: (e?) => any;
		style?: CSSProperties;
	} & UIDisplayAdditionalProps
> = (props) => {
	let displayClass: string = props.displayClassName || "col-sm-6";
	let valueClass: string = "";
	if (!props.noSingleLineDisplay) {
		displayClass = "col-12 col-lg-3 single-row-col";
		valueClass = "col-12 col-lg-8 single-row-col";
	}

	const value: any = stringify(props.value, true);

	const innerContent = (
		<>
			{/* {props.loading && (
				<UIProgressBar
					positionRelative={true}
					text={props.hideLoadingText ? " " : undefined}
					hideLoadingText={props.hideLoadingText}
					style={{ padding: "0px", marginTop: "-3px", marginBottom: "-3px" }}
				/>
			)} */}
			{!props.loading && (
				<span
					className={clsx("d-inline-flex align-items-center w-100", props.className)}
					style={{ wordBreak: "break-all", ...(props.style || {}) }}
					onClick={props.onClick}
				>
					{!!props.displayEmptyMsg && <UIEmptyFieldData />}
					{!props.displayEmptyMsg && value}
				</span>
			)}
		</>
	);

	if (props.displayContentOnly) return innerContent;

	const content = (
		<>
			{!!props.display && (
				<div className={clsx(displayClass, "details-header-col d-inline-flex align-items-center")}>
					<div
						className={clsx("detail-title", props.headerClassName)}
						aria-label={typeof props.display === "string" ? props.display : ""}
					>
						{props.display}
					</div>
				</div>
			)}
			{!props.noSingleLineDisplay && <div className="d-none d-lg-block col-lg-1" />}
			<div
				className={clsx(
					valueClass,
					"ui-display-field-value d-inline-flex align-items-start flex-grow-1",
					props.loading && "px-0"
				)}
			>
				{innerContent}
			</div>
		</>
	);

	if (props.singleLineDisplay) return content;
	return (
		<div
			className={clsx(`ui-display-data-col-${props.id}`, "row", props.rowClassName, props.loading && "pr-0")}
			key={props.id}
		>
			{content}
		</div>
	);
};

export const UIDisplayFields: React.FC<{
	cols: ColDef[];
	singleLineDisplay?: boolean;
	omitFieldIfEmpty?: boolean;
	className?: string;
}> = (props) => {
	let renderedColsMap = {};
	return (
		<div className={clsx("container ui-details-list ui-display-fields", props.className)}>
			{props.cols.map((col: CorreColDef, i: number) => {
				const render: boolean =
					!col.hideInDetailView && !isSystemColExtended(col) && !renderedColsMap[col.colId];
				if (!render) return null;
				renderedColsMap[col.colId] = true;

				return (
					<React.Fragment key={col.field}>
						<UIDisplayFieldData
							key={col.field}
							colId={col.colId}
							singleLineDisplay={props.singleLineDisplay}
							omitLabel={col.quickViewRenderContentOnly}
							omitWrapper={true}
							omitFieldIfEmpty={props.omitFieldIfEmpty}
						/>
						{!!props.singleLineDisplay && i % 2 === 0 && <div key={i} className="w-100-sm d-lg-none"></div>}
					</React.Fragment>
				);
			})}
		</div>
	);
};

export const UIEmptyFieldData: React.FC<{ name?: string | React.ReactNode }> = (props) =>
	props.children ? (
		<>{props.children}</>
	) : (
		<span className="font-italic font-weight-lighter text-light-gray" style={{ fontSize: "90%" }}>
			{!props.name && "[EMPTY]"}
			{!!props.name && `No ${props.name || "Data"}`}
		</span>
	);

function getContextFormattedValue(context: IFormattedViewContext, colId: string): any {
	const fieldViewData: FieldViewData = get(context.viewData, colId);
	if (!fieldViewData) {
		console.warn("No view data for colId:", colId);
		return null;
	}
	return fieldViewData.formattedValue;
}
